import React, { useState, useEffect } from "react";
import FiltersBar from "./FiltersBar.jsx";
import MemberModal from "./MemberModal.jsx";

export default function MembersTable({ token, user }) {
  const initialFilters = {
    district_id: user.role_id === 2 ? user.id : null,
    division_id: user.role_id === 3 ? user.id : null,
    sector_id: user.role_id === 4 ? user.id : null,
    unit_id: user.role_id === 5 ? user.id : null
  };


  const [filters, setFilters] = useState(initialFilters);
  const [data, setData] = useState({ members: [], total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [perPage] = useState(100);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const sortableColumns = {
    name: "Name",
    phone: "Phone",
    district_name: "District",
    division_name: "Division",
    sector_name: "Sector",
    unit_name: "Unit"
  };

  const handleSort = key => {
    if (sortBy === key) {
      setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [units, setUnits] = useState([]);

  const isSectorUser = user.role_id === 4;
  const memberLimit = 33;
  const totalMembers = data.total ?? 0;
  const reachedLimit = isSectorUser && totalMembers >= memberLimit;
  const overLimit = isSectorUser && totalMembers > memberLimit;

  const fetchMembers = async (fullExport = false) => {
    setLoading(true);
    try {
      const queryParams = {
        page: fullExport ? 1 : page,
        per_page: fullExport ? 1000000 : perPage, // fetch all for CSV
        sort_by: sortBy,
        sort_dir: sortDir,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };
      const query = new URLSearchParams(queryParams).toString();
      const res = await fetch(`https://smartcoreapi.ssfkerala.org/analytics/members.php?${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      setData({
        members: json.members ?? [],
        total: json.total ?? 0,
        totalPages: Math.ceil((json.total ?? 0) / perPage)
      });
      return json.members ?? [];
    } catch (err) {
      console.error(err);
      setData({ members: [], total: 0, totalPages: 1 });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    let sectorId = isSectorUser ? user.id : filters.sector_id;
    if (!sectorId) return;
    try {
      const res = await fetch(`https://smartcoreapi.ssfkerala.org/analytics/children.php?parent_id=${sectorId}&level=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      setUnits(json);
    } catch (err) {
      console.error("Failed to fetch units", err);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchUnits();
  }, [filters, page, perPage, sortBy, sortDir]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    try {
      await fetch("https://smartcoreapi.ssfkerala.org/members/delete.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id })
      });
      fetchMembers();
    } catch (err) {
      console.error("Failed to delete member", err);
    }
  };

  // Generate hierarchy string for WhatsApp
  // Generate hierarchy string using actual selected names
const getHierarchy = () => {
  const parts = [];
  if (filters.district_id) {
    const districtName = data.members.find(m => m.district_id === filters.district_id)?.district_name;
    if (districtName) parts.push(districtName);
  }
  if (filters.division_id) {
    const divisionName = data.members.find(m => m.division_id === filters.division_id)?.division_name;
    if (divisionName) parts.push(divisionName);
  }
  if (filters.sector_id) {
    const sectorName = data.members.find(m => m.sector_id === filters.sector_id)?.sector_name;
    if (sectorName) parts.push(sectorName);
  }
  if (filters.unit_id) {
    const unitName = data.members.find(m => m.unit_id === filters.unit_id)?.unit_name;
    if (unitName) parts.push(unitName);
  }
  return parts.join(" → ") || "All Levels";
};


  const handleWhatsAppShare = () => {
    if (data.members.length === 0) return alert("No members on this page to share.");

    const now = new Date();
    const timeString = now.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

    const header = `Future Summit Smart Core
Registered Members
${getHierarchy()}
Report Generated: ${timeString}
Total Members on this page: ${data.members.length}\n`;

    const body = data.members
      .map((m, i) => `${i + 1}. ${m.name} - ${m.phone}`)
      .join("\n");

    const message = `${header}\n${body}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleCSVExport = async () => {
    const allMembers = await fetchMembers(true); // fetch all
    if (allMembers.length === 0) return alert("No members to export");

    const csvRows = [];
    const headers = ["Name", "Phone", "District", "Division", "Sector", "Unit"];
    csvRows.push(headers.join(","));

    allMembers.forEach(m => {
      const row = [
        m.name ?? "",
        m.phone ?? "",
        m.district_name ?? "",
        m.division_name ?? "",
        m.sector_name ?? "",
        m.unit_name ?? ""
      ];
      csvRows.push(row.map(val => `"${val}"`).join(","));
    });

    const csvData = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(csvData);
    link.download = `members_export.csv`;
    link.click();
  };

  // Export all members as a printable PDF (uses browser Print -> Save as PDF)
  const handlePDFExport = async () => {
    const allMembers = await fetchMembers(true);
    if (allMembers.length === 0) return alert("No members to export");

    const now = new Date();
    const timeString = now.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

    const headerHtml = `
      <div style="margin-bottom:12px;">
        <h2 style="margin:0;font-family:Arial,Helvetica,sans-serif">Future Summit Smart Core - Members Report</h2>
        <div style="margin-top:6px;font-family:Arial,Helvetica,sans-serif">${getHierarchy()}</div>
        <div style="margin-top:4px;color:#555;font-family:Arial,Helvetica,sans-serif">Generated: ${timeString}</div>
      </div>
    `;

    const rowsHtml = allMembers.map((m, i) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;text-align:right">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:6px">${m.name ?? ""}</td>
        <td style="border:1px solid #ddd;padding:6px">${m.phone ?? ""}</td>
        <td style="border:1px solid #ddd;padding:6px">${m.district_name ?? ""}</td>
        <td style="border:1px solid #ddd;padding:6px">${m.division_name ?? ""}</td>
        <td style="border:1px solid #ddd;padding:6px">${m.sector_name ?? ""}</td>
        <td style="border:1px solid #ddd;padding:6px">${m.unit_name ?? ""}</td>
      </tr>
    `).join("");
    
    const html = `
      <html>
      <head>
        <title>Members Report</title>
        <meta charset="utf-8" />
        <style>
          body { font-family: Arial, Helvetica, sans-serif; padding: 16px; color: #111; }
          table { border-collapse: collapse; width: 100%; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background: #f7f7f7; text-align: left; }
          @media print {
            button { display: none; }
          }
        </style>
      </head>
      <body>
        ${headerHtml}
        <table>
          <thead>
            <tr>
              <th style="width:40px">#</th>
              <th>Name</th>
              <th>Phone</th>
              <th>District</th>
              <th>Division</th>
              <th>Sector</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    if (!w) {
      alert("Popup blocked. Please allow popups for this site to export PDF.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // give browser a moment to render before opening print dialog
    setTimeout(() => {
      w.focus();
      w.print();
    }, 600);
  };

  return (
    <div className="space-y-4">
      {user.role_id !== 4 && <FiltersBar token={token} user={user} filters={filters} setFilters={setFilters} />}

      <div className="flex flex-wrap gap-2">
        {isSectorUser && (
          <button
            onClick={() => setModalData({})}
            disabled={reachedLimit}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Member
          </button>
        )}
        <button
          onClick={handleWhatsAppShare}
          className="bg-green-500 text-white px-4 py-2 rounded-xl font-medium hover:shadow-lg transition"
        >
          Share on WhatsApp
        </button>
        <button
          onClick={handleCSVExport}
          className="bg-gray-700 text-white px-4 py-2 rounded-xl font-medium hover:shadow-lg transition"
        >
          Export CSV
        </button>

        <button
          onClick={handlePDFExport}
          className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-medium hover:shadow-lg transition"
        >
          Export PDF
        </button>
      </div>

      {overLimit && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
          This sector currently has {totalMembers} members. Please delete members and bring the count down to {memberLimit} or fewer.
        </div>
      )}

      {reachedLimit && !overLimit && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-xl">
          Member limit of {memberLimit} reached. Delete existing members to add new ones.
        </div>
      )}

      {modalData && <MemberModal data={modalData} setModalData={setModalData} units={units} token={token} refresh={fetchMembers} />}

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {Object.entries(sortableColumns).map(([key, label]) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer select-none"
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {sortBy === key && (
                            <span>{sortDir === "asc" ? "▲" : "▼"}</span>
                          )}
                        </span>
                      </th>
                    ))}
                    {isSectorUser && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.members.length > 0 ? data.members.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.phone}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.district_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.division_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.sector_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.unit_name}</td>
                      {isSectorUser && (
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setModalData(m)}
                              className="px-3 py-1 bg-blue-100 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-200 transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(m.id)}
                              className="px-3 py-1 bg-red-100 text-red-600 rounded-lg text-xs font-medium hover:bg-red-200 transition"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={isSectorUser ? 8 : 7} className="px-4 py-8 text-center text-gray-500">
                        No members found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between bg-white rounded-xl shadow-sm p-4">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {data.totalPages}
            </span>
            <button
              disabled={page >= data.totalPages}
              onClick={() => setPage(p => p + 1)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
